import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";
import { fecha, fechaMes, finMes, ymd } from "./calendario";

const SHEET = "FE Contratos";
const TABLE = "Contratos";

const CLIENTES = [
  "Grupo Altamira", "Comercial Norte", "Distribuidora Luna", "Hotel Mirador", "Farmacias Vida",
  "Constructora Roble", "Café La Esquina", "Papelería Central",
] as const;

const rnd = seededRandom(2902);
// Some contracts start on the 29th–31st: the month-end edge case of FECHA.MES.
const INICIOS = [
  fecha(2025, 1, 31), fecha(2025, 8, 31), fecha(2025, 10, 30), fecha(2025, 12, 29),
  ...Array.from({ length: 24 }, () => fecha(2024, 6, 1) + rnd.int(0, 700)),
];
// Contrato, Cliente, Inicio, Plazo (meses), Días de gracia
const ROWS = INICIOS.map((inicio, i) => [
  `CT-${301 + i}`,
  CLIENTES[i % CLIENTES.length],
  inicio,
  rnd.pick([1, 6, 12, 12, 18, 24]),
  rnd.pick([0, 5, 10, 15]),
] as [string, string, number, number, number]).sort((a, b) => a[2] - b[2]);

const CORTE = fecha(2026, 6, 30);
type Row = (typeof ROWS)[number];
const fin = (r: Row) => fechaMes(r[2], r[3]);

export const spec: FuncionProjectSpec = {
  projectId: "funciones-contratos",
  sheets: [
    {
      name: SHEET,
      title: "Contratos y vencimientos",
      subtitle: "Contratos de servicio — calcula fechas de fin, cortes de mes y estatus",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Contrato", "Cliente", "Inicio", "Plazo (meses)", "Días de gracia"],
          rows: ROWS,
          answerColumns: ["Fin de contrato", "Cierre de mes", "Primer pago", "Días del mes", "Inicio mes siguiente", "Estado", "Límite con gracia"],
          formats: {
            Inicio: "dd/mm/yyyy",
            "Fin de contrato": "dd/mm/yyyy",
            "Cierre de mes": "dd/mm/yyyy",
            "Primer pago": "dd/mm/yyyy",
            "Inicio mes siguiente": "dd/mm/yyyy",
            "Límite con gracia": "dd/mm/yyyy",
          },
        },
      ],
      inputs: [{ address: "O4", label: "Fecha de corte", value: CORTE, format: "dd/mm/yyyy" }],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Fin de contrato", fns: ["EDATE"],
      expected: ROWS.map(fin),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Cierre de mes", fns: ["EOMONTH"],
      expected: ROWS.map((r) => finMes(r[2], 0)),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Primer pago", fns: ["EOMONTH"],
      expected: ROWS.map((r) => finMes(r[2], 1)),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Días del mes", fns: ["DAY", "EOMONTH"],
      expected: ROWS.map((r) => ymd(finMes(r[2], 0)).d),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Inicio mes siguiente", fns: ["EOMONTH"],
      expected: ROWS.map((r) => finMes(r[2], 0) + 1),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Estado", fns: ["IFS|IF"], exact: true,
      expected: ROWS.map((r) => (fin(r) < CORTE ? "Vencido" : fin(r) <= CORTE + 60 ? "Por vencer" : "Vigente")),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Límite con gracia", fns: ["EOMONTH"],
      expected: ROWS.map((r) => finMes(fin(r) + r[4], 0)),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-contratos",
  title: "FECHA.MES y FIN.MES: Contratos y vencimientos",
  subtitle: "Sumar meses de verdad, no 30 días",
  description:
    "Administras contratos de servicio con plazos de 1 a 24 meses. Necesitas la fecha de fin de cada uno, el cierre de mes para facturar, el primer pago y un estatus contra la fecha de corte.\n\nSumar 30 días por mes falla porque los meses no miden lo mismo. FECHA.MES suma meses respetando el calendario y FIN.MES devuelve el último día de un mes. Fíjate en los contratos que empiezan el 31: FECHA.MES del 31/01 + 1 mes da 28/02.",
  topics: [
    "FECHA.MES: sumar o restar meses",
    "FIN.MES: último día del mes (0 = este mes)",
    "Cuántos días tiene un mes",
    "Primer día del mes siguiente",
    "Estatus contra una fecha de corte",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Fin del contrato",
      description: "En la hoja <strong>FE Contratos</strong>, columna <strong>Fin de contrato</strong>: suma el <strong>Plazo (meses)</strong> a la fecha de <strong>Inicio</strong>.",
      solution: [
        `Selecciona ${F("F5")}.`,
        "FECHA.MES(fecha, meses) mueve la fecha esos meses. Si el día no existe en el mes destino (31 de febrero), usa el último día del mes.",
        `Escribe ${F("=FECHA.MES([@Inicio],[@[Plazo (meses)]])")}.`,
        "Busca el contrato que inicia el 31/01/2025: ¿qué fecha de fin te dio?",
      ],
      expectedResult: "Inicio + plazo en meses",
      finalFormula: "=FECHA.MES([@Inicio],[@[Plazo (meses)]])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Cierre del mes de inicio",
      description: "En la columna <strong>Cierre de mes</strong>: devuelve el <strong>último día del mes</strong> en que empezó el contrato.",
      solution: [
        `Selecciona ${F("G5")}.`,
        `FIN.MES(fecha, meses): con ${F("0")} da el último día del mismo mes.`,
        `Escribe ${F("=FIN.MES([@Inicio],0)")}.`,
      ],
      expectedResult: "30/06, 31/07, 28/02…",
      finalFormula: "=FIN.MES([@Inicio],0)",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Primer pago",
      description: "En la columna <strong>Primer pago</strong>: el primer pago vence el <strong>último día del mes siguiente</strong> al inicio.",
      solution: [`Selecciona ${F("H5")}.`, `Escribe ${F("=FIN.MES([@Inicio],1)")}.`],
      expectedResult: "Último día del mes siguiente",
      finalFormula: "=FIN.MES([@Inicio],1)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: ¿Cuántos días tiene el mes?",
      description: "En la columna <strong>Días del mes</strong>: cuántos días tiene el mes en que inició el contrato (28, 29, 30 o 31).",
      solution: [
        `Selecciona ${F("I5")}.`,
        "El último día del mes es justamente el número de días que tiene. Saca el día con DIA.",
        `Escribe ${F("=DIA(FIN.MES([@Inicio],0))")}.`,
      ],
      expectedResult: "28 a 31",
      finalFormula: "=DIA(FIN.MES([@Inicio],0))",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Primer día del mes siguiente",
      description: "En la columna <strong>Inicio mes siguiente</strong>: el <strong>día 1 del mes siguiente</strong> al inicio del contrato.",
      solution: [
        `Selecciona ${F("J5")}.`,
        "El día siguiente al último día del mes siempre es el día 1 del mes que sigue.",
        `Escribe ${F("=FIN.MES([@Inicio],0)+1")}.`,
      ],
      expectedResult: "01/07/2025 si inició el 15/06/2025",
      finalFormula: "=FIN.MES([@Inicio],0)+1",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Estado del contrato",
      description: conditions(
        "En la columna <strong>Estado</strong>: compara el <strong>Fin de contrato</strong> con la <strong>Fecha de corte</strong> de <strong>O4</strong>:",
        [
          "Terminó <strong>antes</strong> del corte → <strong>Vencido</strong>",
          "Termina en los <strong>próximos 60 días</strong> (hasta corte + 60) → <strong>Por vencer</strong>",
          "Termina después → <strong>Vigente</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        "En la vida real usarías HOY() en lugar de O4; aquí usamos una fecha fija para que el resultado no cambie cada día.",
        `Escribe ${F('=SI.CONJUNTO([@[Fin de contrato]]<$O$4,"Vencido",[@[Fin de contrato]]<=$O$4+60,"Por vencer",VERDADERO,"Vigente")')}.`,
      ],
      expectedResult: "Vencido / Por vencer / Vigente",
      finalFormula:
        '=SI.CONJUNTO([@[Fin de contrato]]<$O$4,"Vencido",[@[Fin de contrato]]<=$O$4+60,"Por vencer",VERDADERO,"Vigente")',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Límite con días de gracia",
      description: conditions("En la columna <strong>Límite con gracia</strong>: el cliente puede pagar hasta el <strong>cierre del mes</strong> en que caiga:", [
        "Fin de contrato <strong>+</strong> Días de gracia",
        "Llevado al <strong>último día de ese mes</strong>",
      ]),
      solution: [`Selecciona ${F("L5")}.`, `Escribe ${F("=FIN.MES([@[Fin de contrato]]+[@[Días de gracia]],0)")}.`],
      expectedResult: "Fin de mes después de los días de gracia",
      finalFormula: "=FIN.MES([@[Fin de contrato]]+[@[Días de gracia]],0)",
    },
  ],
};
